import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid';
import { Fragment, isValidElement, type ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';
import { Separator } from '../../separator/Separator';
import { Text } from '../../text';
import { Tooltip } from '../../tooltip/Tooltip';
import { DropdownMenuContent } from './DropdownMenuContent';
import { DropdownMenuItem } from './DropdownMenuItem';
import { DropdownMenuRoot } from './DropdownMenuRoot';
import { DropdownMenuSeparator } from './DropdownMenuSeparator';
import { DropdownMenuTrigger } from './DropdownMenuTrigger';

type Props = {
  children: ReactNode;
  label?: ReactNode;
  disabled?: boolean;
  menuItems: {
    key: string;
    hasBottomSeparator?: boolean;
    item: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    /**
     * 補足説明をツールチップで表示する（Text コンポーネントの hint と同じ API）
     * disabled 時の理由表示などに使用
     */
    hint?: { value: string; className?: string; hidden?: boolean };
  }[];
  triggerClassName?: string;
  contentClassName?: string;
  contentAlign?: 'start' | 'end' | 'center';
  showLabelBottomSeparator?: boolean;
};

export const DropdownMenu = ({
  children,
  label,
  disabled,
  menuItems,
  triggerClassName,
  contentClassName,
  contentAlign = 'end',
  showLabelBottomSeparator,
}: Props) => {
  return (
    <DropdownMenuRoot>
      <DropdownMenuTrigger
        asChild
        disabled={disabled}
        className={cn(
          'focus:ring-2 focus:ring-focus-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
          triggerClassName,
        )}
      >
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={contentAlign}
        className={cn('inset-shadow-xs bg-surface-base', contentClassName)}
      >
        {isValidElement(label) ? (
          label
        ) : label ? (
          <Text variant="description" color="muted" as="label">
            {label}
          </Text>
        ) : null}
        {showLabelBottomSeparator && <Separator />}
        <div>
          {menuItems.map(
            ({ key, hasBottomSeparator, item, onClick, disabled: itemDisabled, hint }) => {
              const showHint = hint && !hint.hidden;
              return (
                <Fragment key={key}>
                  <DropdownMenuItem onClick={onClick} disabled={itemDisabled}>
                    <span
                      className={cn('inline-flex items-center gap-1', showHint && hint?.className)}
                    >
                      {item}
                      {showHint && (
                        <Tooltip tooltipContent={hint.value} contentClassName="whitespace-pre-line">
                          {/* DropdownMenuItem の disabled は pointer-events-none を当てるため、
                              ツールチップ用アイコンには明示的に pointer-events-auto を再付与する */}
                          <span className="pointer-events-auto inline-flex">
                            <QuestionMarkCircleIcon
                              className="size-3.5 shrink-0 text-text-muted"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </span>
                  </DropdownMenuItem>
                  {hasBottomSeparator && <DropdownMenuSeparator />}
                </Fragment>
              );
            },
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
};
